import React from "react";
import {
  FaInfoCircle,
  FaRecycle,
  FaSort,
  FaSortDown,
  FaSortUp,
} from "react-icons/fa";

import { getFormulaShortLabel, formatWeight } from "../utils/repartitionUtils";
import { PER_PAGE_OPTIONS } from "../data/repartitionData";

const thClass =
  "px-2 py-2 text-left text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-300";
const tdClass =
  "border-t border-slate-100 px-2 py-1.5 text-xs text-slate-800 dark:border-white/10 dark:text-slate-100 whitespace-nowrap";

const Tag = ({ children, className = "" }) => (
  <span
    className={`inline-flex items-center justify-center rounded-full px-2 py-0.5 text-[10px] font-bold ${className}`}
  >
    {children}
  </span>
);

const GenderTag = ({ value }) => {
  if (value === "M") {
    return (
      <Tag className="bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300">
        M
      </Tag>
    );
  }

  if (value === "F") {
    return (
      <Tag className="bg-pink-100 text-pink-700 dark:bg-pink-500/15 dark:text-pink-300">
        F
      </Tag>
    );
  }

  return (
    <Tag className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
      —
    </Tag>
  );
};

const needsClear = (row) => {
  const hasDistribution =
    row.is_distributed || row.distribution_id != null || row._distId != null;

  const hasResult =
    Number(row.classement_position || 0) > 0 ||
    Number(row.total_victoires || 0) > 0 ||
    Number(row.total_points || 0) > 0;

  const disqualified = !!Number(row.is_disqualified || 0);

  return Boolean(hasDistribution || hasResult || disqualified);
};

const RepartitionTable = ({
  rows,
  distributions,
  selectedIds,
  toggleSelect,
  allVisibleSelected,
  toggleSelectAllVisible,
  onOpenDistribution,

  page,
  perPage,
  total,
  totalPages,
  onPageChange,
  onPerPageChange,

  sortBy,
  sortDir,
  onToggleSortDist,

  onClearCompetiteur,
  clearingIds = [],
}) => {
  const isClearing = (id) => clearingIds.includes(id);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-2 shadow-sm dark:border-white/10 dark:bg-slate-900">
      <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-white/10">
        <table className="min-w-[1320px] w-full bg-white text-xs dark:bg-slate-900">
          <thead className="sticky top-0 z-10 bg-slate-100 dark:bg-slate-800">
            <tr>
              <th className={thClass}>
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAllVisible}
                  className="h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                />
              </th>

              <th className={thClass}>Nom</th>
              <th className={thClass}>Prénom</th>
              <th className={thClass}>Genre</th>
              <th className={thClass}>Poids</th>
              <th className={thClass}>Cat. âge</th>
              <th className={thClass}>Cat. poids</th>
              <th className={thClass}>Ceinture</th>
              <th className={thClass}>Club</th>
              <th className={thClass}>Discipline</th>
              <th className={thClass}>Type</th>

              <th className={thClass}>
                <button
                  type="button"
                  onClick={onToggleSortDist}
                  className="inline-flex items-center gap-1 transition hover:text-orange-600"
                  title="Trier par numéro de distribution"
                >
                  Dist.
                  {sortBy !== "dist" ? (
                    <FaSort />
                  ) : sortDir === "asc" ? (
                    <FaSortUp />
                  ) : (
                    <FaSortDown />
                  )}
                </button>
              </th>

              <th className={thClass}>Statut</th>
              <th className={thClass}>Tapis</th>
              <th className={`${thClass} text-right`}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row) => {
              const distId = row._distId ?? row.distribution_id ?? null;
              const isDistributed = distId != null;

              const distribution = isDistributed
                ? distributions.find(
                    (item) => String(item.id) === String(distId)
                  )
                : null;

              const formula = getFormulaShortLabel(
                distribution?.formule_repartition
              );

              const selected = selectedIds.includes(row.id);

              return (
                <tr
                  key={row.id}
                  className={`transition ${
                    selected
                      ? "bg-orange-50 dark:bg-orange-500/10"
                      : row.genre_competiteur === "M"
                      ? "hover:bg-blue-50/60 dark:hover:bg-blue-500/5"
                      : row.genre_competiteur === "F"
                      ? "hover:bg-pink-50/60 dark:hover:bg-pink-500/5"
                      : "hover:bg-slate-50 dark:hover:bg-white/5"
                  }`}
                >
                  <td className={tdClass}>
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleSelect(row.id)}
                      className="h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                    />
                  </td>

                  <td className={`${tdClass} font-bold`}>
                    {row.nom_competiteur}
                  </td>
                  <td className={tdClass}>{row.prenom_competiteur}</td>
                  <td className={tdClass}>
                    <GenderTag value={row.genre_competiteur} />
                  </td>

                  <td className={tdClass}>
                    <Tag className="bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                      {formatWeight(row.poids_competiteur)}
                    </Tag>
                  </td>

                  <td className={tdClass}>{row._ageCat || "—"}</td>
                  <td className={tdClass}>{row._weightCat || "—"}</td>
                  <td className={tdClass}>{row.grade_competiteur || "—"}</td>

                  <td className={`${tdClass} max-w-[220px] truncate`} title={row._club}>
                    {row._club || "—"}
                  </td>

                  <td className={tdClass}>{row._discipline}</td>
                  <td className={tdClass}>{formula}</td>

                  <td className={tdClass}>
                    {row._distLocalNumber ? (
                      <Tag className="bg-slate-900 text-white dark:bg-white dark:text-slate-950">
                        #{row._distLocalNumber}
                      </Tag>
                    ) : (
                      "—"
                    )}
                  </td>

                  <td className={tdClass}>
                    {isDistributed ? (
                      <button
                        type="button"
                        onClick={() => onOpenDistribution(distId)}
                        className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold text-blue-700 transition hover:bg-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:hover:bg-blue-500/25"
                      >
                        <FaInfoCircle className="h-3 w-3" />
                        Réparti
                      </button>
                    ) : (
                      <Tag className="bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300">
                        NR
                      </Tag>
                    )}
                  </td>

                  <td className={tdClass}>{distribution?.numero_tapis ?? "—"}</td>

                  <td className={`${tdClass} text-right`}>
                    {needsClear(row) && (
                      <button
                        type="button"
                        onClick={() => onClearCompetiteur(row)}
                        disabled={isClearing(row.id)}
                        className={`inline-flex min-h-7 items-center gap-1 rounded-lg px-2 py-1 text-[10px] font-bold text-white transition ${
                          isClearing(row.id)
                            ? "cursor-not-allowed bg-orange-300 opacity-70"
                            : "bg-orange-500 hover:bg-orange-600"
                        }`}
                      >
                        <FaRecycle className="h-3 w-3" />
                        {isClearing(row.id) ? "Reset…" : "Reset"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}

            {!rows.length && (
              <tr>
                <td
                  colSpan={15}
                  className="px-3 py-10 text-center text-sm text-slate-500 dark:text-slate-400"
                >
                  Aucun compétiteur selon les filtres actuels.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-slate-600 dark:text-slate-300">
          Page <span className="font-bold">{page}</span> / {totalPages} —{" "}
          {total} ligne{total > 1 ? "s" : ""}
        </p>

        <div className="flex items-center gap-2">
          <select
            value={perPage}
            onChange={(event) => onPerPageChange(Number(event.target.value))}
            className="h-9 rounded-xl border border-slate-200 bg-white px-2 text-xs font-bold text-slate-800 dark:border-white/10 dark:bg-slate-900 dark:text-white"
          >
            {PER_PAGE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}/page
              </option>
            ))}
          </select>

          <div className="inline-flex overflow-hidden rounded-xl border border-slate-200 dark:border-white/10">
            <button
              type="button"
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page <= 1}
              className="h-9 px-3 text-xs font-bold transition hover:bg-slate-100 disabled:opacity-40 dark:hover:bg-white/10"
            >
              ◀
            </button>

            <button
              type="button"
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
              className="h-9 px-3 text-xs font-bold transition hover:bg-slate-100 disabled:opacity-40 dark:hover:bg-white/10"
            >
              ▶
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RepartitionTable;